import * as React from "react";
import { NavItem, NavLink } from "reactstrap";

export interface IIconTextButtonProps {
  icon: string;
  text?: string;
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  tooltip?: string;
  options?: any;
}

const useOutsideClick = (ref, handler) => {
  React.useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
};

const IconTextButton: React.SFC<IIconTextButtonProps> = ({
  icon,
  text,
  onClick,
  disabled,
  tooltip,
  options
}) => {
  const toggleRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [menuStyle, setMenuStyle] = React.useState({ top: 0, left: 0, minWidth: 0 });

  const computePosition = React.useCallback(() => {
    const toggle = toggleRef.current;
    if (!toggle) return;

    const rect = toggle.getBoundingClientRect();
    const minWidth = Math.max(rect.width, 150);
    const top = rect.bottom + 4;
    const left = rect.left;

    setMenuStyle({ top: Math.round(top), left: Math.round(left), minWidth: Math.round(minWidth) });
  }, []);

  // reposition on open, scroll or resize
  React.useEffect(() => {
    if (!open) return;
    computePosition();
    const onScroll = () => computePosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, computePosition]);

  const toggleDropdown = () => {
    if (disabled) return;
    setOpen(!open);
  };

  useOutsideClick(toggleRef, () => setOpen(false));

  return (
      <div className={["IconTextButton", !!disabled && "disabled"].join(' ')} onClick={toggleDropdown} title={tooltip} ref={toggleRef} >
          <i data-testid={"icon-text-btn"} className={[icon, !!disabled && "disabledIcon"].join(' ')} />
        {text}
        <span style={{ marginLeft: "6px" }} className="caret"></span>
        <span className="sr-only">Toggle Dropdown</span>
        
        {open && 
          <div
            ref={menuRef}
            className="saveButtonPortalMenu dropdown-menu show"
            style={{
              position: "fixed",
              top: menuStyle.top,
              left: menuStyle.left,
              minWidth: menuStyle.minWidth,
              zIndex: 2000,
            }}
            role="menu"
          >
            {options.map((opt, idx) => (
              <NavItem key={idx}>
                <NavLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (disabled) return;
                    opt.onClick && opt.onClick();
                    setOpen(false);
                  }}
                  className={disabled ? "disabled" : ""}
                >
                  {opt.label}
                </NavLink>
              </NavItem>
            ))}
          </div>
        }
      </div>
  );
};

export default IconTextButton;
